import axios from 'axios';
import {
  AUTH_LOGIN,
  AUTH_LOGIN_FAILED,
  AUTH_LOGIN_SUCCESS,
  AUTH_LOGIN_NEW_PASSWORD_REQUIRED,
  AUTH_LOGIN_TOTP_REQUIRED,
  AUTH_2FA_VERIFY,
  AUTH_2FA_VERIFY_SUCCESS,
  AUTH_2FA_VERIFY_FAILED,
  AUTH_GET_WORKSPACES,
  AUTH_GET_WORKSPACES_SUCCESS,
  AUTH_GET_WORKSPACES_FAILED,
  AUTH_CHANGE_WORKSPACE,
  AUTH_CHANGE_WORKSPACE_SUCCESS,
  AUTH_CHANGE_WORKSPACE_FAILED,
  AUTH_MANAGE_NOTIFICATIONS,
  AUTH_MANAGE_NOTIFICATIONS_SUCCESS,
  AUTH_MANAGE_NOTIFICATIONS_FAILED,
} from './types';
import { apiAction } from '../../api/actions';
import { setSession } from '../../session/actions';
import * as CognitoAuthService from '../../services/CognitoAuthService';
import config from '../../config';

// Holds the CognitoUser instance between the initial authenticateUser call
// and the completeNewPasswordChallenge follow-up. Module-level because the
// instance is non-serializable and shouldn't live in the Redux store.
let pendingChallengeUser = null;

// Exposed for tests / debug only — do not call directly from app code.
export const __unstable = {
  resetPendingChallenge: () => { pendingChallengeUser = null; },
};

export function login(email, password) {
  return async (dispatch) => {
    dispatch({ type: AUTH_LOGIN });
    try {
      const result = await CognitoAuthService.login(email, password);

      if (result.status === 'NEW_PASSWORD_REQUIRED') {
        pendingChallengeUser = result.user;
        dispatch({
          type: AUTH_LOGIN_NEW_PASSWORD_REQUIRED,
          payload: { email, userAttributes: result.userAttributes },
        });
        return;
      }

      if (result.status === 'TOTP_REQUIRED' || result.status === 'MFA_REQUIRED') {
        pendingChallengeUser = result.user;
        dispatch({
          type: AUTH_LOGIN_TOTP_REQUIRED,
          payload: { email, challengeName: result.challengeName },
        });
        return;
      }

      await completeLogin(dispatch, result.session);
    } catch (err) {
      dispatch({
        type: AUTH_LOGIN_FAILED,
        payload: { error: { name: err.name, code: err.code, message: err.message || 'Login failed' } },
      });
    }
  };
}

// Respond to a Cognito MFA challenge after login. Uses the cached CognitoUser
// from the login() call and the challengeName stored in Redux state so that
// both SOFTWARE_TOKEN_MFA and SMS_MFA routes use the correct mfaType.
export function sendCognitoMFACode(code) {
  return async (dispatch, getState) => {
    if (!pendingChallengeUser) {
      dispatch({
        type: AUTH_LOGIN_FAILED,
        payload: { error: { message: 'Session expired, please sign in again' } },
      });
      return;
    }
    try {
      const { challengeName } = getState().user.cognitoTotpChallenge;
      const result = await CognitoAuthService.sendMFACode(pendingChallengeUser, code, challengeName || 'SOFTWARE_TOKEN_MFA');
      pendingChallengeUser = null;
      await completeLogin(dispatch, result.session);
    } catch (err) {
      dispatch({
        type: AUTH_LOGIN_FAILED,
        payload: { error: { name: err.name, code: err.code, message: err.message || 'Invalid code' } },
      });
    }
  };
}

export function completeNewPassword(newPassword, userAttributes = {}) {
  return async (dispatch) => {
    if (!pendingChallengeUser) {
      dispatch({
        type: AUTH_LOGIN_FAILED,
        payload: { error: { message: 'Session expired, please sign in again' } },
      });
      return;
    }
    try {
      const result = await CognitoAuthService.completeNewPasswordChallenge(
        pendingChallengeUser,
        newPassword,
        userAttributes,
      );
      pendingChallengeUser = null;

      if (result.status === 'MFA_REQUIRED' || result.status === 'TOTP_REQUIRED') {
        // After completing the new-password challenge Cognito can chain into
        // an MFA challenge if the user pool requires it. Store the user and
        // surface the same TOTP UI as a fresh login.
        pendingChallengeUser = result.user;
        dispatch({
          type: AUTH_LOGIN_TOTP_REQUIRED,
          payload: { challengeName: result.challengeName },
        });
        return;
      }

      await completeLogin(dispatch, result.session);
    } catch (err) {
      dispatch({
        type: AUTH_LOGIN_FAILED,
        payload: { error: { name: err.name, code: err.code, message: err.message || 'Failed to set new password' } },
      });
    }
  };
}

// Bootstrap the Redux session after a successful Cognito authentication:
//   1. Use the access token immediately for the initial bootstrap calls.
//   2. Fetch workspaces (tenant-agnostic — backend resolves by cognitoSub).
//   3. Pick a default tenant (first workspace; the tenant switcher UI lands later).
//   4. Fetch /auth/me with X-Tenant-Id to get the inflated user.
//   5. Persist the final session and emit AUTH_LOGIN_SUCCESS.
async function completeLogin(dispatch, session) {
  const accessToken = session.getAccessToken().getJwtToken();
  const refreshToken = session.getRefreshToken().getToken();

  const authHeader = { Authorization: `Bearer ${accessToken}` };

  const workspacesResponse = await axios.get(`${config.apiBaseUrl}/su/auth/get-workspaces`, {
    headers: authHeader,
  }).catch((err) => {
    if (err.response?.status === 404) return { data: [] };
    throw err;
  });
  const workspaces = workspacesResponse.data || [];
  if (workspaces.length === 0) {
    dispatch({
      type: AUTH_LOGIN_FAILED,
      payload: { error: { message: 'No workspaces available for this account' } },
    });
    return;
  }

  const { tenant } = workspaces[0];

  const meResponse = await axios.get(`${config.apiBaseUrl}/su/auth/me`, {
    headers: { ...authHeader, 'X-Tenant-Id': tenant },
  });
  const user = meResponse.data;

  const finalSession = { token: accessToken, refreshToken, tenant, user };
  dispatch(setSession(finalSession));
  dispatch({ type: AUTH_LOGIN_SUCCESS, payload: { ...finalSession, workspaces } });
  dispatch({ type: AUTH_GET_WORKSPACES_SUCCESS, payload: workspaces });
}

export function verify2FA(dispatch, accountId, token) {
  return apiAction({
    resource: '/su/auth/2fa-verify',
    method: 'POST',
    data: {
      accountId,
      token
    },
    onSuccess: (data) => onSuccessVerify2FA(dispatch, data),
    onFailure: (error) => onFailedVerify2FA(error),
    carry: {
      label: AUTH_2FA_VERIFY
    },
  });
}

function onSuccessVerify2FA(dispatch, data) {
  dispatch(setSession(data));
  return {
    type: AUTH_2FA_VERIFY_SUCCESS,
    payload: data
  };
}

function onFailedVerify2FA(error) {
  return {
    type: AUTH_2FA_VERIFY_FAILED,
    payload: {
      error
    }
  };
}

export function getWorkspaces(onFailure) {
  return apiAction({
    resource: '/su/auth/get-workspaces',
    method: 'GET',
    onSuccess: (data) => onSuccessGetWorkspaces(data),
    onFailure: (error) => onFailedGetWorkspaces(error, onFailure),
    carry: {
      label: AUTH_GET_WORKSPACES
    },
  });
}

function onSuccessGetWorkspaces(data) {
  return {
    type: AUTH_GET_WORKSPACES_SUCCESS,
    payload: data
  };
}

function onFailedGetWorkspaces(error, onFailure) {
  if (onFailure) onFailure();
  return {
    type: AUTH_GET_WORKSPACES_FAILED,
    payload: {
      error
    }
  };
}

export function changeWorkspace(dispatch, workspaceId, onSuccess, onFailure) {
  return apiAction({
    resource: '/su/auth/change-workspace',
    method: 'POST',
    data: {
      workspaceId,
    },
    onSuccess: (data) => onSuccessChangeWorkspace(dispatch, data, onSuccess),
    onFailure: (error) => onFailedChangeWorkspace(error, onFailure),
    carry: {
      label: AUTH_CHANGE_WORKSPACE
    },
  });
}

function onSuccessChangeWorkspace(dispatch, data, onSuccess) {
  dispatch(setSession(data));
  if (onSuccess) onSuccess();
  return {
    type: AUTH_CHANGE_WORKSPACE_SUCCESS,
    payload: data
  };
}

function onFailedChangeWorkspace(error, onFailure) {
  if (onFailure) onFailure();
  return {
    type: AUTH_CHANGE_WORKSPACE_FAILED,
    payload: {
      error
    }
  };
}

export function refreshUser(dispatch, session) {
  return apiAction({
    resource: '/su/auth/me',
    method: 'GET',
    onSuccess: (data) => onSuccessRefreshUser(dispatch, session, data),
    onFailure: (error) => onFailedRefreshUser(error),
    carry: {
      label: AUTH_MANAGE_NOTIFICATIONS
    },
  });
}

function onSuccessRefreshUser(dispatch, session, data) {
  dispatch(setSession({
    ...session,
    user: data
  }));
  return {
    type: AUTH_MANAGE_NOTIFICATIONS_SUCCESS,
    payload: data
  };
}

function onFailedRefreshUser(error) {
  return {
    type: AUTH_MANAGE_NOTIFICATIONS_FAILED,
    payload: {
      error
    }
  };
}
